<template>
  <Label class="radio-card" @click="onCardClick">
    <input
      type="radio"
      :checked="isChecked"
      :name="name"
      :value="value"
      :disabled="disabled"
      @change="onChange"
      @keydown.space.prevent="onChange($event)"
      @keydown.enter.prevent="onChange($event)"
      tabindex="0"
      class="radio-card-input"
    />
    <slot />
  </Label>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import Label from './label.vue';

interface RadioCardProps {
  name: string;
  value: any;
  modelValue: any;
  disabled?: boolean;
}

const props = withDefaults(defineProps<RadioCardProps>(), {
  disabled: false,
});

const emit = defineEmits(['update:modelValue']);
const isChecked = computed(() => props.value === props.modelValue);

function onChange(event: Event) {
  const newValue = (event.target as HTMLInputElement).checked ? props.value : null;
  emit('update:modelValue', newValue);
}

function onCardClick(event: MouseEvent) {
  // Avoid double-trigger when clicking the hidden input
  if ((event.target as HTMLElement).closest('.radio-card-input')) return;
  if (props.disabled) return;
  emit('update:modelValue', props.value);
}
</script>

<style scoped lang="scss">
.radio-card {
  display: flex;
  align-items: flex-start;
  padding: var(--t-spacing-md);
  width: 100%;
  position: relative;

  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-md);
  background: var(--t-color-neutral-white);
  cursor: pointer;

  &:has(input:checked) {
    border-color: var(--t-color-neutral-950);
  }

  &:has(input:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:has(input:focus-visible) {
    border-color: var(--t-color-neutral-950);
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }

  &:hover:not(:has(input:disabled)) {
    border-color: var(--t-color-neutral-700);
  }
}

.radio-card-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  pointer-events: none;
}
</style>
